import channels from "./channels";
import { icon, node, text } from "~/util/dom";

const UNREAD = 5; // vterin po ktere je i ctena nectena

function isToday(date) {
	const now = new Date();

	return ["getFullYear", "getMonth", "getDate"].every(method => date[method]() === now[method]());
}

function isYesterday(dateIn) {
	const date = new Date(dateIn.getTime());

	date.setDate(date.getDate()+1);

	return isToday(date);
}

function formatDate(date) {
	return `${date.getDate()}. ${date.getMonth()+1}. ${date.getFullYear()}`;
}

function formatTime(date) {
	const hours = date.getHours().toString();
	let minutes = date.getMinutes().toString();

	if (minutes.length < 2) {
		minutes = `0${minutes}`;
	}

	return `${hours}:${minutes}`;
}

function formatDateTime(ts) {
	const date = new Date(ts * 1000);
	const time = formatTime(date);

	if (isToday(date)) {
		return `Dnes ${time}`;
	} else if (isYesterday(date)) {
		return `Včera ${time}`;
	}

	return `${formatDate(date)} ${time}`;
}

function formatPlural(count) {
	switch (count) {
		case 1: return `další ${count} uživatel`;
		case 2:
		case 3:
		case 4: return `další ${count} uživatelé`;
		default: return `dalších ${count} uživatelů`;
	}
}

function formatVerb(count, variants) {
	switch (count) {
		case 0: return variants[0];
		case 1: return variants[1];
		case 2:
		case 3:
		case 4: return variants[2];
		default: return variants[3];
	}
}

function formatActionText(channelSuffix, totalUsers) {
	switch (channelSuffix) {
		case "following": {
			const verb = formatVerb(totalUsers-1, ["začal/a", "začal", "začali", "začalo"]);

			return `${verb} sledovat vaše příspěvky`;
		}

		default: {
			const verb = formatVerb(totalUsers-1, ["reagoval/a", "reagoval", "reagovali", "reagovalo"]);

			return `${verb} na váš příspěvek`;
		}
	}
}

function buildLink(data, stack, parent) {
	parent.appendChild(node("div", { className: "blind" }, "Přejít do"));
	const link = node("a", { href: data.link, target: "_blank" });

	parent.appendChild(link);
	link.addEventListener("click", () => stack.onClick(data));

	return link;
}

function buildGenericHeader(data, parent) {
	const channel = channels[data.channel];

	if (channel) {
		const channelInfo = node("div", { className: "channel-info" });
		const logo = node("div", { className: "channel-logo" });

		parent.appendChild(channelInfo);
		channelInfo.appendChild(logo);
		logo.appendChild(node("img", { src: data.channelIcon || channel.icon }));
		channelInfo.appendChild(node("h3", {}, data.title || channel.name));
		channelInfo.appendChild(node("div", { className: "created" }, formatDateTime(data.created)));
	}
}

function buildGeneric(data, parent) {
	buildGenericHeader(data, parent);

	data.subject && parent.appendChild(node("p", {}, data.subject));
	data.content && parent.appendChild(node("p", { className: "content" }, data.content));
}

function buildZboziRating(data, parent, stack) {
	buildGenericHeader(data, parent);
	data.content && parent.appendChild(node("p", { className: "content" }, data.content));
	const attrs = data.attrs.rating;
	const rating = document.createElement("div");

	parent.append(rating);
	rating.classList.add("rating");

	["yes", "yesbut", "no"].forEach(rate => {
		const labels = { yes: "Ano", yesbut: "Ano, ale…", no: "Ne" };
		const link = document.createElement("a");

		link.href = attrs.url.replace("{}", rate);
		link.target = "_blank";
		link.append(icon(`rating-${rate}`));
		link.append(labels[rate]);

		link.addEventListener("click", () => stack.onClick(data));
		rating.append(link);
	});
}

function buildZboziStars(data, parent, stack) {
	buildGenericHeader(data, parent);
	data.content && parent.appendChild(node("p", { className: "content" }, data.content));
	const attrs = data.attrs.stars;

	if (data.link) {
		const link = buildLink(data, stack, parent);
		const img = new Image();

		img.crossOrigin = "anonymous";
		img.src = attrs.productImgUrl;
		link.append(img);
	}

	const stars = document.createElement("div");

	parent.append(stars);
	stars.classList.add("stars");

	const nodes = [];

	function highlight(count) {
		nodes.forEach((element, index) => {
			element.classList.toggle("active", index < count);
		});
	}

	for (let index = 0; index < 5; index++) {
		const star = document.createElement("a");

		star.href = attrs.url.replace("{}", index + 1);
		star.target = "_blank";
		star.append(icon("star-filled"));
		star.append(icon("star-empty"));
		stars.append(star);
		nodes.push(star);
		star.addEventListener("mouseover", () => highlight(index + 1));
		star.addEventListener("click", () => stack.onClick(data));
	}
	stars.addEventListener("mouseleave", () => highlight(0));
	highlight(0);
}

function buildReactions(data, parent) {
	const header = node("div", { className: "sn-header" });
	const avatar = node("div", { className: "avatar" });
	const h3 = node("h3");

	parent.appendChild(header);
	header.appendChild(avatar);
	header.appendChild(h3);

	const reactions = data.attrs.reactions;
	const users = reactions.users;
	const first = users[0];
	const channelSuffix = data.channel.split(".").pop();
	const actionText = formatActionText(channelSuffix, reactions.totalUsers);

	if (reactions.totalUsers === 1) {
		avatar.appendChild(node("img", { src: `${first.avatar}/32` }));
		h3.appendChild(node("strong", {}, first.name));
		h3.appendChild(text(` ${actionText}`));
	} else {
		users.slice(0, 2).reverse().forEach(user => {
			avatar.appendChild(node("img", { src: `${user.avatar}/24` }));
		});
		const count = reactions.totalUsers-1;

		h3.appendChild(node("strong", {}, `${first.name} a ${formatPlural(count)}`));
		h3.appendChild(text(` ${actionText}`));
	}

	data.content && parent.appendChild(node("p", { className: "sn-content" }, data.content));

	const paragraph = node("p", { className: "sn-footer" });
	const channel = channels[data.channel];

	parent.appendChild(paragraph);

	if (channel) {
		const src = data.channelIcon || channel.icon;

		paragraph.appendChild(node("img", { src }));
		paragraph.appendChild(text(data.title || channel.name));
		paragraph.appendChild(node("span", { className: "bullet" }, "•"));
	}
	paragraph.appendChild(text(formatDateTime(data.created)));
}

export default class Notification {
	node: HTMLLIElement;

	constructor(data, stack) {
		this.node = node("li");
		this.node.setAttribute("data-dot", `item-${data.channel}`);

		const isUnread = data.displayed ? (Date.now() / 1000) - data.displayed < UNREAD: true;

		isUnread && this.node.classList.add("unread");

		data.link && this.node.classList.add("clickable");
		data.clicked && this.node.classList.add("clicked");

		const close = node("button", { type: "button" });

		close.appendChild(icon("cross"));
		this.node.appendChild(close);
		close.setAttribute("aria-label", "Zavřít oznámení");
		close.setAttribute("data-dot", "close");
		close.addEventListener("click", event => {
			event.preventDefault(); // klikani na odkaz
			event.stopPropagation(); // nechceme posluchac na cele notifikaci
			this.node.parentNode && this.node.parentNode.removeChild(this.node); // fixme radeji .remove(), ale IE
			stack.onRemove(data);
		});

		let parent: HTMLElement = this.node;

		const attrs = data.attrs || {};

		if (attrs.reactions) {
			this.node.setAttribute("data-type", "reactions");
			if (data.link) {
				parent = buildLink(data, stack, parent);
			}
			buildReactions(data, parent);
		} else if (attrs.stars) {
			this.node.setAttribute("data-type", "zbozi-stars");
			this.node.classList.add("clickable");
			buildZboziStars(data, parent, stack);
		} else if (attrs.rating) {
			this.node.setAttribute("data-type", "zbozi-rating");
			this.node.classList.add("clickable");
			buildZboziRating(data, parent, stack);
		} else {
			this.node.setAttribute("data-type", "generic");
			if (data.link) {
				parent = buildLink(data, stack, parent);
			}
			buildGeneric(data, parent);
		}
	}
}
